import { useState, useRef, useEffect } from "react";
import Store from "../../../Stores/Store";
import style from "./UpBalance.module.scss";
import Heading from "../../Typography/Heading/Heading";
import Text from "../../Typography/Text/Text";
import {
  inputFocusMessage,
  closeIframeMessage,
} from "../../../Utils/iframeMessenger";
import Button from "../../Button/Button";
import LsBalance from "./LsBalance/LsBalance";
import toMainImg from "../../../Static/images/toMainImg.svg";
import {
  formatMoneyOnBlur,
  formatMoneyDisplay,
  formatMoneyValue,
  applyMoneyInputValue,
  focusMoneyInputSelection,
  handleMoneyInputKeyDown,
  handleMoneyInputBeforeInput,
} from "../../../Utils/moneyInput";
import postDistribute from "../../../API/postDistribute";

function getAccountServicesBalance(account) {
  return account.services.reduce((acc, item) => {
    if (item.amount > 0) {
      return acc + item.amount + item.penaltie;
    }
    return acc;
  }, 0);
}

function buildInitialLsAmounts(accounts) {
  return accounts.map((account) => {
    const balance = getAccountServicesBalance(account);
    return balance > 0 ? formatMoneyValue(balance) : "0,00";
  });
}

function parseTotalAmount(value) {
  return Number(String(value).replace(",", ".")) || 0;
}

function amountsMatch(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  return a.every((v, i) => parseTotalAmount(v) === parseTotalAmount(b[i]));
}

function rublesToKopecks(amountRub) {
  return Math.round(Number(amountRub) * 100) || 0;
}

function calculateTaxKopecks(amountRub, taxPercent) {
  if (!(taxPercent > 0)) return 0;
  return Math.round(rublesToKopecks(amountRub) * taxPercent);
}

const UpBalance = ({ setModalUpBalanceActive, balanceInfo, debt }) => {
  const totalInputRef = useRef(null);
  const initialTotalValue =
    debt >= 0 ? "0,00" : formatMoneyValue(Math.abs(debt));

  const [totalInputValue, setTotalInputValue] = useState(initialTotalValue);
  const [lsAmounts, setLsAmounts] = useState(() =>
    buildInitialLsAmounts(balanceInfo.accounts),
  );
  const [totalTaxValue, setTotalTaxValue] = useState(0);
  const [isCalculated, setIsCalculated] = useState(false);
  const [calculatedTotal, setCalculatedTotal] = useState(null);
  const [calculatedLsAmounts, setCalculatedLsAmounts] = useState(null);
  const [isManualMode, setIsManualMode] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [isDistributeLoading, setIsDistributeLoading] = useState(false);

  useEffect(() => {
    const initialTotal = parseTotalAmount(initialTotalValue);
    if (initialTotal > 0) {
      applyCalculation(lsAmounts, initialTotalValue);
    }
  }, []);

  const manualTotal = lsAmounts.reduce(
    (sum, value) => sum + parseTotalAmount(value),
    0,
  );
  const currentTotal = isManualMode
    ? manualTotal
    : parseTotalAmount(totalInputValue);

  const isAutoStale =
    !isManualMode &&
    isCalculated &&
    calculatedTotal !== null &&
    parseTotalAmount(totalInputValue) !== parseTotalAmount(calculatedTotal);

  const isManualStale =
    isManualMode &&
    isCalculated &&
    calculatedLsAmounts !== null &&
    !amountsMatch(lsAmounts, calculatedLsAmounts);

  const isStale = isAutoStale || isManualStale;

  const needsCalculation = !isCalculated && currentTotal > 0;
  const needsRecalculation = isStale;

  function backToBalance() {
    setModalUpBalanceActive(false);
  }

  function computeTaxValue(amounts) {
    let taxKopecks = 0;
    amounts.forEach((amountStr, id) => {
      const tax_percent = balanceInfo.accounts[id]?.tax_percent;
      taxKopecks += calculateTaxKopecks(
        parseTotalAmount(amountStr),
        tax_percent,
      );
    });
    return taxKopecks / 100;
  }

  function distributeByWeights(total, weightValues) {
    const totalNum = Number(total ?? 0);
    const weights = (weightValues || []).map((v) => Number(v ?? 0));
    const weightSum = weights.reduce((s, w) => s + w, 0);
    const normalizedWeights =
      weightSum > 0
        ? weights.map((w) => w / weightSum)
        : weights.length
          ? weights.map(() => 1 / weights.length)
          : [];
    const raws = normalizedWeights.map((w) => w * totalNum);
    const floored = raws.map(
      (r) => Math.floor((r + Number.EPSILON) * 100) / 100,
    );
    const sumFloored = floored.reduce((s, x) => s + x, 0);
    let remainderCents = Math.round((totalNum - sumFloored) * 100);
    if (remainderCents <= 0) {
      return floored.map((x) => Number(x.toFixed(2)));
    }
    const fractionalRanks = raws.map((r, idx) => {
      const cents = r * 100;
      const frac = cents - Math.floor(cents);
      return { idx, frac };
    });
    fractionalRanks.sort((a, b) => {
      if (b.frac === a.frac) return a.idx - b.idx;
      return b.frac - a.frac;
    });
    const result = floored.slice();
    let i = 0;
    while (remainderCents > 0) {
      const targetIdx = fractionalRanks[i % fractionalRanks.length].idx;
      result[targetIdx] = Number((result[targetIdx] + 0.01).toFixed(2));
      remainderCents--;
      i++;
    }
    return result.map((x) => Number(x.toFixed(2)));
  }

  function computeProportionalShares(totalValue, weightAmounts) {
    const valuesArray = weightAmounts.map((v) => String(v).replace(",", "."));
    const array = distributeByWeights(totalValue, valuesArray);
    return array.map((x) => formatMoneyValue(x));
  }

  function resetCalculationState() {
    setIsCalculated(false);
    setCalculatedTotal(null);
    setCalculatedLsAmounts(null);
    setLsAmounts(buildInitialLsAmounts(balanceInfo.accounts));
    setTotalTaxValue(0);
  }

  function applyCalculation(amounts, totalFormatted) {
    setLsAmounts(amounts);
    setTotalInputValue(totalFormatted);
    setTotalTaxValue(computeTaxValue(amounts));
    setCalculatedTotal(totalFormatted);
    setCalculatedLsAmounts([...amounts]);
    setIsCalculated(true);

    if (totalInputRef.current) {
      totalInputRef.current.value = totalFormatted;
      totalInputRef.current.dataset.moneyLastValue = totalFormatted;
    }
  }

  function buildDistributePayload(totalRub) {
    const payload = {
      date: new Date().toISOString(),
      amount: Math.round(totalRub * 100),
      payment_method_id: 8,
      accounts: [],
    };

    if (balanceInfo.unified_number) {
      payload.unified_number = String(balanceInfo.unified_number);
    } else {
      payload.code = String(Store.payment.accountInfo.number);
    }

    return payload;
  }

  function parseDistributeResponse(response) {
    if (!response || response === "error") return null;
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return null;
  }

  function mapDistributeToAmounts(distributedAccounts) {
    const amountsByPa = new Map();

    distributedAccounts.forEach((item) => {
      const id = String(item?.id_pa);
      if (!id) return;
      amountsByPa.set(
        id,
        (amountsByPa.get(id) || 0) + Number(item?.amount ?? 0),
      );
    });

    return balanceInfo.accounts.map((account) => {
      const accountId = String(account.id_pa ?? account.id_PA);
      const kopecks = amountsByPa.get(accountId) ?? 0;
      return formatMoneyValue(kopecks / 100);
    });
  }

  async function handleCalculate() {
    if (currentTotal <= 0) return;

    if (isManualMode) {
      const lssInputs = document.getElementsByName("lsInput");
      const amounts = Array.from(lssInputs).map((item) => item.value);
      const total = amounts.reduce(
        (sum, value) => sum + parseTotalAmount(value),
        0,
      );
      applyCalculation(amounts, formatMoneyValue(total));
      return;
    }

    setIsDistributeLoading(true);
    const response = await postDistribute(buildDistributePayload(currentTotal));
    setIsDistributeLoading(false);

    const distributedAccounts = parseDistributeResponse(response);
    const newAmounts = distributedAccounts?.length
      ? mapDistributeToAmounts(distributedAccounts)
      : computeProportionalShares(currentTotal, lsAmounts);

    applyCalculation(newAmounts, totalInputValue);
  }

  function totalInputChange() {
    const input = totalInputRef.current;
    const filteredValue = applyMoneyInputValue(input);
    if (!input || filteredValue === null) return;

    setTotalInputValue(input.value);

    if (parseTotalAmount(input.value) === 0) {
      resetCalculationState();
    }
  }

  function totalInputFocus(e) {
    inputFocusMessage("openNumKeyboard");
    focusMoneyInputSelection(e.target);
  }

  function totalInputBlur() {
    inputFocusMessage("closeKeyboard");

    const input = totalInputRef.current;
    if (!input) return;

    const formatted = formatMoneyOnBlur(input.value);
    input.value = formatted;
    input.dataset.moneyLastValue = formatted;
    setTotalInputValue(formatted);

    if (parseTotalAmount(formatted) === 0) {
      resetCalculationState();
    }
  }

  function lsInputChange() {
    const lssInputs = document.getElementsByName("lsInput");
    const amounts = Array.from(lssInputs).map((item) => item.value);
    const totalValue = amounts.reduce(
      (sum, value) => sum + parseTotalAmount(value),
      0,
    );

    setLsAmounts(amounts);
    setTotalInputValue(formatMoneyValue(totalValue));

    if (totalInputRef.current) {
      const formattedTotal = formatMoneyValue(totalValue);
      totalInputRef.current.value = formattedTotal;
      totalInputRef.current.dataset.moneyLastValue = formattedTotal;
    }
  }

  function handleToggleClick(e) {
    e.preventDefault();
    setConfirmModal(isManualMode ? "auto" : "manual");
  }

  function closeConfirmModal() {
    setConfirmModal(null);
  }

  function confirmManualMode() {
    setIsManualMode(true);
    setCalculatedLsAmounts(isCalculated ? [...lsAmounts] : null);
    setConfirmModal(null);
  }

  function confirmAutoMode() {
    const rollbackTotal =
      isStale && calculatedTotal !== null ? calculatedTotal : totalInputValue;

    setIsManualMode(false);
    setTotalInputValue(rollbackTotal);
    setConfirmModal(null);

    if (totalInputRef.current) {
      totalInputRef.current.value = rollbackTotal;
      totalInputRef.current.dataset.moneyLastValue = rollbackTotal;
    }

    if (isStale && calculatedLsAmounts !== null) {
      setLsAmounts([...calculatedLsAmounts]);
      setTotalTaxValue(computeTaxValue(calculatedLsAmounts));
    } else {
      const total = parseTotalAmount(rollbackTotal);
      if (total > 0) {
        const newAmounts = computeProportionalShares(total, lsAmounts);
        applyCalculation(newAmounts, rollbackTotal);
      } else {
        resetCalculationState();
      }
    }
  }

  function handleConfirmModalYes() {
    if (confirmModal === "manual") {
      confirmManualMode();
      return;
    }
    if (confirmModal === "auto") {
      confirmAutoMode();
    }
  }

  function setCorrectPaymentInfo(paymentInfo) {
    let tempData = paymentInfo;
    tempData.type = "payment";
    tempData.is_infomat = true;
    tempData.payment_method_id = 8;
    delete tempData.totalNegativeBalance;
    delete tempData.totalPositiveBalance;
    if (tempData.unified_number) {
      tempData.unified_id = Store.payment.accounts.id_unified_account;
    } else {
      tempData.unified_id = null;
      tempData.unified_number = null;
      tempData.code = Store.payment.accountInfo.number;
    }
    const gateInfo = tempData.accounts.map((account, idx) => {
      const amountRub = parseTotalAmount(lsAmounts[idx]);
      const baseAmount = rublesToKopecks(amountRub);
      const tax_amount = calculateTaxKopecks(amountRub, account.tax_percent);
      return {
        ls: String(account.number),
        positions: [
          {
            name: account.service_name,
            amount: baseAmount,
            tax_amount,
          },
        ],
      };
    });
    tempData.accounts.forEach((account, idx) => {
      delete account.totalAccountNegativeBalance;
      delete account.totalAccountPositiveBalance;
      delete account.number;
      delete account.id_org;
      delete account.service_name;
      delete account.services;
      const amountRub = parseTotalAmount(lsAmounts[idx]);
      const baseAmount = rublesToKopecks(amountRub);
      const tax_amount = calculateTaxKopecks(amountRub, account.tax_percent);
      account.tax_amount = tax_amount;
      account.amount = baseAmount + tax_amount;
      delete account.tax_percent;
    });
    tempData.email = "";

    tempData.amount = tempData.accounts.reduce(
      (sum, account) => sum + account.amount,
      0,
    );
    tempData.gate = {
      amount: tempData.amount,
      cost: tempData.amount,
      info: gateInfo,
    };
    Store.payment.setTerminalPaymentInfo(tempData);
  }

  function toPaymentWindow() {
    if (currentTotal > 0 && isCalculated && !isStale) {
      setCorrectPaymentInfo(JSON.parse(JSON.stringify(balanceInfo)));
      Store.payment.setAmount(Store.payment.terminalPaymentInfo.amount);
      Store.payment.setPaymentWindowActive(true);
    }
  }

  function getDisplayedTotal() {
    if (isStale && calculatedTotal !== null) {
      return formatMoneyDisplay(parseTotalAmount(calculatedTotal));
    }
    return formatMoneyDisplay(currentTotal);
  }

  function sum_with_tax_percent() {
    const baseTotal =
      isStale && calculatedTotal !== null
        ? parseTotalAmount(calculatedTotal)
        : currentTotal;

    if (baseTotal >= 0 && totalTaxValue >= 0) {
      const totalKopecks =
        rublesToKopecks(baseTotal) + rublesToKopecks(totalTaxValue);
      return formatMoneyDisplay(totalKopecks / 100);
    }
  }

  function getButtonLabel() {
    if (needsRecalculation) return "Пересчитать";
    if (needsCalculation) return "Рассчитать";
    return "Далее";
  }

  async function handleMainButton() {
    if (needsRecalculation || needsCalculation) {
      await handleCalculate();
      return;
    }
    toPaymentWindow();
  }

  return (
    <div className={style.modalUpBalance}>
      <div
        className={`${style.modalUpBalance__wrap} ${confirmModal ? style.__blurred : ""}`}
      >
        <div className={style.modalUpBalance__wrap__back}>
          <div className={style.toMain} onClick={() => closeIframeMessage()}>
            <img src={toMainImg} alt="" />
            <Text size="medium">Главная</Text>
          </div>
          <div className={style.devider}>/</div>
          <div className={style.back} onClick={() => backToBalance()}>
            <Text size="medium">Назад</Text>
          </div>
        </div>

        <div className={style.modalUpBalance__wrap__title}>
          <Heading level="h3">
            {balanceInfo?.unified_number
              ? `ЕЛС № ${balanceInfo.unified_number}`
              : `ЛС № ${Store.payment.accountInfo.number}`}
          </Heading>
        </div>
        <div className={style.modalUpBalance__wrap__content}>
          {balanceInfo.accounts.map((item, idx) => {
            return (
              <LsBalance
                lsBalanceInfo={item}
                lsInputChange={lsInputChange}
                isManualMode={isManualMode}
                displayAmount={lsAmounts[idx]}
                isStale={!isManualMode && isStale}
                key={`${idx}-${isManualMode ? "manual" : "auto"}`}
              />
            );
          })}
          <div className={style.modalUpBalance__wrap__content__total}>
            <div className={style.manualRow}>
              <span className={style.manualTitle}>
                Распределить суммы вручную
              </span>
              <label className={style.switch} onClick={handleToggleClick}>
                <input type="checkbox" checked={isManualMode} readOnly />
                <span className={style.slider}></span>
              </label>
            </div>
            {isManualMode ? (
              <div className={style.taxRow}>
                <span className={style.taxLabel}>Итого</span>
                <div className={style.taxValueWrapper}>
                  <span className={isStale ? style.stale : ""}>
                    {getDisplayedTotal()}
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className={style.title}>Итого</div>
                <label className={style.inputWrapper}>
                  <input
                    ref={totalInputRef}
                    defaultValue={totalInputValue}
                    onChange={totalInputChange}
                    onKeyDown={handleMoneyInputKeyDown}
                    onBeforeInput={handleMoneyInputBeforeInput}
                    className={style.sumInput}
                    onFocus={totalInputFocus}
                    onBlur={totalInputBlur}
                    maxLength={12}
                  />
                  <span className={style.rubSuffix} aria-hidden="true">
                    руб
                  </span>
                </label>
              </>
            )}
          </div>
          <div className={style.modalUpBalance__wrap__content__taxBlock}>
            <div className={style.taxRow}>
              <span className={style.taxLabel}>Комиссия</span>
              <div className={style.taxValueWrapper}>
                <span className={isStale ? style.stale : ""}>
                  {formatMoneyDisplay(totalTaxValue)}
                </span>
              </div>
            </div>

            <div className={style.taxRow}>
              <span className={style.taxLabel}>Сумма к оплате</span>
              <div className={style.taxValueWrapper}>
                <span className={isStale ? style.stale : ""}>
                  {isCalculated || isStale
                    ? sum_with_tax_percent()
                    : formatMoneyDisplay(0)}
                </span>
              </div>
            </div>
          </div>
          <Button
            clickHandler={handleMainButton}
            disabled={currentTotal === 0 || isDistributeLoading}
            loading={isDistributeLoading}
          >
            {getButtonLabel()}
          </Button>
        </div>
      </div>

      {confirmModal && (
        <div className={style.confirmModal} onClick={closeConfirmModal}>
          <div
            className={style.confirmModal__wrap}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={style.confirmModal__close}
              onClick={closeConfirmModal}
              aria-label="Закрыть"
            />
            <div className={style.confirmModal__content}>
              <div className={style.confirmModal__title}>
                {confirmModal === "manual"
                  ? "Распределить суммы вручную?"
                  : "Распределить суммы автоматически?"}
              </div>
              <div className={style.confirmModal__subTitle}>
                {confirmModal === "manual"
                  ? "При ручном распределении возможна неполная оплата задолженности и начисление пеней"
                  : "Система автоматически распределит платеж для полного погашения задолженности и снижения риска начисления пеней"}
              </div>
              <div className={style.confirmModal__btns}>
                <button
                  type="button"
                  className={`${style.confirmModal__btn} ${style.primary}`}
                  onClick={handleConfirmModalYes}
                >
                  Да
                </button>
                <button
                  type="button"
                  className={`${style.confirmModal__btn} ${style.secondary}`}
                  onClick={closeConfirmModal}
                >
                  Нет
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpBalance;
